import { ICRMUser } from './crm-user';
import { ICustomerUser } from './customer.dto';

export interface PhenotypicAgeInputs {
  age: number;
  albumin: number;
  creatinine: number;
  glucose: number;
  crp: number;
  lymph_pct: number;
  mcv: number;
  rdw: number;
  alp: number;
  wbc: number;
}

export interface PhenotypicAgeResult {
  phenotypicAge: number | null;
  errors?: string[];
}

export interface PhenotypicAgeAnalysis {
  bioAge: number;
  actualAge: number;
  lastCalculatedAt: Date | string;
}

export interface HomaIrAnalysis {
  value: number;
  fastingGlucoseMmolL: number;
  fastingInsulinUiuMl: number;
  lastCalculatedAt: Date | string;
}

export interface TygAnalysis {
  value: number;
  fastingGlucoseMgDl: number;
  triglycerideMgDl: number;
  lastCalculatedAt: Date | string;
}
export interface Analyses {
  phenotypicAge?: PhenotypicAgeAnalysis;
  homaIr?: HomaIrAnalysis;
  tyg?: TygAnalysis;
}

export type LabResultSource = 'fax' | 'patient_upload' | 'manual';
export type LabResultUploadOrigin =
  | 'patient_portal'
  | 'emr_admin'
  | 'unknown';

/** Per-field confidence from Llama (0–1), stored when extraction includes field_metadata. */
export interface LabExtractFieldConfidence {
  parsing_confidence?: number;
  extraction_confidence?: number;
  confidence?: number;
}

export interface LabResultMeta {
  orderId?: string; // this CarePortal order id
  junctionOrderId?: string; // this Junction order id
  source?: string;
  /** Document-level AI parsing confidence (0–1) from Llama extraction_metadata. */
  extractionConfidence?: number;
  /** Worst per-field confidence (0–1) from Llama field_metadata; conservative QA signal. */
  worstFieldConfidence?: number;
  /** Sex from extraction, normalized when persisted (male / female / other). */
  patientSex?: 'male' | 'female' | 'other';
  /** Who initiated patient_upload: patient portal vs EMR admin upload. */
  uploadOrigin?: LabResultUploadOrigin;
  /** EMR uploader display name when uploaded by admin from EMR. */
  uploadedByName?: string;
  [key: string]: any;
}
export class LabResult {
  _id: string;
  customer: string | ICustomerUser;
  verifiedBy: string | ICRMUser;
  faxId: string;
  runId: string;
  docUrl: string;
  collectedAt: Date;
  labName: string;
  results: LabTestResult[];
  analyses?: Analyses;
  language: 'EN' | 'FR';
  organization: string;
  source?: LabResultSource;
  labTestIds: string[];
  createdAt: Date;
  updatedAt: Date;
  meta?: LabResultMeta;
  __v?: number;
}

export type ILabResultDTO = Partial<
  Omit<LabResult, '_id' | 'createdAt' | 'updatedAt'>
>;

/**
 * Core lab-extraction payload used by our APIs/services.
 * Adapters (e.g. Llama) should map their vendor-specific shapes to this.
 */
export interface LabResultPatientData {
  lab_name?: string | null;
  patient_hc?: string | null;
  phone_number?: string | null;
  phone?: string | null; // legacy / alternate
  first_name?: string | null;
  last_name?: string | null;
  sex?: string | null;
  date_of_birth?: string | null;
  date_of_collection?: string | null;
  page_start?: number | null;
  page_end?: number | null;

  /** Internal: Llama field_metadata map for this patient (not persisted on patient row). */
  _field_confidence?: Record<string, LabExtractFieldConfidence>;
  /** Internal: document parsing confidence (0–1). */
  _overall_confidence?: number;
  /** Internal: worst per-field confidence (0–1) from field_metadata. */
  _worst_field_confidence?: number;
  /** Internal: upload origin propagated from patient-upload workflow/event. */
  _upload_origin?: LabResultUploadOrigin;
  /** Internal: uploader display name (EMR uploads). */
  _uploaded_by_name?: string;

  // Freeform catch-all for extracted lab values (wbc, rbc, etc)
  [key: string]:
    | string
    | number
    | null
    | undefined
    | Record<string, LabExtractFieldConfidence>;
}

export type LabTestResultStatus = 'normal' | 'optimal' | 'out-of-range' | 'abnormal';

export class LabTestResult {
  testName: string;
  value: number;
  units: string;
  status?: LabTestResultStatus;
  comparisonOperator?: string;
  notes?: string;
  refRange?: string;
  /** Combined field confidence (parsing × extraction), 0–1. */
  confidence?: number;
}

export interface LabResultQueryDto {
  id?: string;
  customer?: string;
  customerName?: string;
  labName?: string;
  verifiedBy?: boolean;
  verifiedStatus?: 'all' | 'verified' | 'not-verified';
  source?: LabResultSource;
  minCreatedDate?: string | Date;
  maxCreatedDate?: string | Date;
  minCollectedDate?: string | Date;
  maxCollectedDate?: string | Date;
  limit?: number;
  offset?: number;
  sortField?: string;
  sortOrder?: number;
}
